import React, { useState, useEffect, useMemo, useCallback } from 'react'
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import './bulkAssignment.css'
import { module as misc } from "@st/misc";
import { module as inviteCode } from "@st/inviteCode";
import { module as activity } from "@st/activity";
import { module as ui } from "@st/ui";
import { connectStore } from 'redux-box';
import inviteCodeApi from '../../../services/api/inviteCode';
import userApi from '../../../services/api/user';
import debounce from 'lodash.debounce'
import { GRADE_LEVEL_MAPPING, READER_TYPES } from '../../../utils/constant';
import { ThemeButton } from "../../../static/styled/custom.styled";
import DatePicker from "react-datepicker";
import {
    DateSelector,
    DateSelectorIcon,
} from "../../flive-code-view/renewalold-school/RenewalOldSchool.styled";
import moment from "moment";
import swal from 'sweetalert2';

const BulkAssignmentModal = (props) => {
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [grades, setGrades] = useState([]);
    const [selectedSchools, setSelectedSchools] = useState([]);
    const [selectedGrades, setSelectedGrades] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [checkedKeys, setCheckedKeys] = useState([]);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedReaderType, setSelectedReaderType] = useState(null)
    const [deadline, setDeadline] = useState(null)
    const [optionsCache, setOptionsCache] = useState([]);

    useEffect(() => {
        let content = props.misc.assignment_content;
        let content_type = props.misc.assignment_content_type;
        let gradeList = props.activity.grades;

        if (content_type === 'story' || content_type === "news") {
            let grade_level = content.grade_level;

            // Step 1: Get unique levels from grade_level (ignore difficulty part)
            const uniqueLevels = Array.from(
                new Set(
                    grade_level.map((lvl) => lvl.split(":")[0])
                )
            );

            // Step 2: Map each level to grade
            const grades = uniqueLevels
                .map((level) => {
                    const grade_name = GRADE_LEVEL_MAPPING[level];
                    return gradeList.find((grd) => grd.name === grade_name);
                })
                .filter(Boolean); // Remove undefined in case of mapping errors

            // Step 3: Convert to Select-compatible format
            const gradesList = grades.map((grd) => ({
                label: grd.label,
                value: grd.id
            }));
            setGrades(gradesList);

        }
        else if (content_type === 'activity' || content_type === 'guidedpath-unit') {
            let grade = content.grade
            let grade_obj = gradeList.find((grd) => grd.id === grade)
            const gradesList = [{
                label: grade_obj.label,
                value: grade_obj.id
            }];
            setGrades(gradesList);
        }
        else if (content_type === 'quiz') {
            let grades = content.grade
            const gradesObj = grades
                .map((id) => {
                    return gradeList.find((grd) => grd.id === id);
                })
                .filter(Boolean); // Remove undefined in case of mapping errors

            // Step 3: Convert to Select-compatible format
            const gradesList = gradesObj.map((grd) => ({
                label: grd.label,
                value: grd.id
            }));
            setGrades(gradesList);
        }
    }, []);

    useEffect(() => {
        const fetchTreeData = async () => {
            if (!selectedSchools.length || !selectedGrades.length) return;

            const tree = [];
            const keysToExpand = [];

            for (let school of selectedSchools) {
                const schoolKey = `school-${school.value}`;
                keysToExpand.push(schoolKey);

                const schoolNode = {
                    title: school.label,
                    key: schoolKey,
                    children: [],
                    disabled: true
                };

                for (let grade of selectedGrades) {
                    const gradeKey = `school-${school.value}-grade-${grade.value}`;
                    keysToExpand.push(gradeKey);

                    let data = {
                        school: school.value,
                        grade: grade.value
                    }
                    const res = await userApi.getSections(data)

                    const gradeNode = {
                        title: grade.label,
                        key: `school-${school.value}-grade-${grade.value}`,
                        disabled: true,
                        children: res ? Object.keys(res).map((section) => ({
                            title: res[section],
                            key: `${section}`,
                            isLeaf: true,
                        })) : [],
                    };

                    schoolNode.children.push(gradeNode);
                }

                tree.push(schoolNode);
            }

            setTreeData(tree);
            setExpandedKeys(keysToExpand);
        };

        fetchTreeData();
    }, [selectedSchools, selectedGrades]);

    const fetchSchools = async (inputValue) => {
        let input = inputValue
        if (!input) return []

        try {
            const res = await inviteCodeApi.getAdminSchoolList(`name=${input}`)

            if (res && res.success && res.result.length > 0) {
                return res.result.map((school) => ({
                    label: `${school.name} - ${school.school_code}`,
                    value: school.id,
                }));
            } else return []
        } catch (err) {
            console.error('Error fetching schools:', err);
            return [];
        }
    };

    const debouncedFetcher = useCallback(
        debounce((inputValue, callback) => {
            fetchSchools(inputValue).then((newOptions) => {
                setOptionsCache(newOptions); // save in state
                callback(newOptions);
            });
        }, 500),
        []
    );

    const loadOptions = (inputValue, callback) => {
        if (!inputValue && optionsCache.length > 0) {
            // if nothing typed, reuse cached options
            callback(optionsCache);
        } else {
            debouncedFetcher(inputValue, callback);
        }
    };

    const customStyles = {
        menu: (provided, state) => ({
            ...provided,
            position: "relative"
        }),
        option: (base, state) => ({
            ...base,
            cursor: 'pointer', //  this sets the pointer on options
        }),
        clearIndicator: (base) => ({
            ...base,
            cursor: 'pointer', //  this makes the clear (x) icon also clickable
        }),
        dropdownIndicator: (base) => ({
            ...base,
            cursor: 'pointer', //  this makes the dropdown arrow clickable
        }),
    };

    const handleCreateAssignment = () => {
        let schools = selectedSchools.map((school) => school.value)
        let grades = selectedGrades.map((grade) => grade.value)
        let sections = checkedKeys
        let data = {}
        if (sections.length > 0) {
            data.school_class = sections
        }
        else if (grades.length > 0 && schools.length > 0) {
            data.grades = grades
            data.schools = schools
        }
        else {
            swal("Oops", "Please select atleast 1 school and grade to proceed.", 'error')
            return
        }

        data.content_id = props.misc.assignment_content.id
        data.content_type = props.misc.assignment_content_type
        data.name = title
        data.description = description
        data.deadline = deadline
        data['bulk_create'] = true

        if (selectedReaderType) {
            data.reader_type = selectedReaderType.value
        }

        props.misc.createBulkAssignment(data)

        if (!props.misc.creating_assignment) {
            // props.ui.hideModal()
        }
    }

    const handleDeadlineChange = (date) => {
        setDeadline(moment(date).format("YYYY-MM-DD"))
    };

    function onDatepickerRef(el) {
        if (el && el.input) {
            el.input.readOnly = true;
        }
    }

    useEffect(() => {
        if (props.misc.assignment_created) {
            props.ui.hideModal();
            props.misc.setAssignmentCreated(false)
            swal("Done!","Assignment assigned successfully.",'success')
        }
    }, [props.misc.assignment_created])

    return (
        <div className="bulk-assignment-container">
            <h2 className="bulk-heading">Bulk Assignment</h2>

            <div className='bulk-assignment-modal-content'>
                <div className="input-group">
                    <input
                        type="text"
                        placeholder="Enter assignment name"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="input-field"
                    />
                    <input
                        type="text"
                        placeholder="Enter description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input-field"
                    />
                </div>

                <div className="selector-group">
                    <div className="selector">
                        <label>Content Name</label>
                        <input
                            type="text"
                            // placeholder="Enter assignment name"
                            value={props.misc.assignment_content && props.misc.assignment_content.name}
                            // onChange={(e) => setTitle(e.target.value)}
                            className="input-field"
                            disabled={true}
                        />
                    </div>

                    <div className="selector">
                        <label>Select Reader Type</label>
                        <Select
                            options={READER_TYPES}
                            value={selectedReaderType}
                            onChange={setSelectedReaderType}
                            placeholder="Select Reader Type"
                            styles={customStyles}
                            menuPlacement="auto" // auto, top, or bottom
                            menuPosition="fixed"
                            isClearable={true}
                        />
                    </div>
                </div>

                <div className="selector-group">
                    <div className="selector">
                        <label>Select Schools</label>
                        <AsyncSelect
                            isMulti={true}
                            cacheOptions={true}
                            loadOptions={loadOptions}
                            onChange={setSelectedSchools}
                            value={selectedSchools}
                            placeholder="Search and select schools"
                            menuCloseOnSelect={false}
                            defaultOptions={optionsCache.length > 0 ? optionsCache : true}
                            closeMenuOnSelect={false}
                            styles={customStyles}
                            menuPlacement="auto" // auto, top, or bottom
                            menuPosition="fixed"
                        />
                    </div>

                    <div className="selector">
                        <label>Select Grades</label>
                        <Select
                            isMulti
                            options={grades}
                            value={selectedGrades}
                            onChange={setSelectedGrades}
                            placeholder="Select Grades"
                            styles={customStyles}
                            menuPlacement="auto" // auto, top, or bottom
                            menuPosition="fixed"
                        />
                    </div>
                </div>
                <div className="selector-group">
                    <div className="selector">
                        <label>Deadline</label>
                        <DateSelector
                            style={{ overflow: "hidden", border: "1px solid #a349c9" }}
                        >
                            <DateSelectorIcon className="fa fa-calendar" />
                            <DatePicker
                                selected={deadline}
                                selectsStart
                                startDate={deadline}
                                onChange={handleDeadlineChange}
                                minDate={moment().toDate()}
                                placeholderText="Select Deadline"
                                ref={(el) => onDatepickerRef(el)}
                            />
                        </DateSelector>
                    </div>
                </div>



                {treeData.length > 0 && (
                    <div className="tree-section">
                        <label>Select Sections</label>
                        <Tree
                            checkable
                            onCheck={(keys) => setCheckedKeys(keys)}
                            checkedKeys={checkedKeys}
                            treeData={treeData}
                            defaultExpandAll
                            autoExpandParent={true}
                            showIcon={false}
                            expandedKeys={expandedKeys}
                            onExpand={setExpandedKeys}
                        />
                    </div>
                )}
            </div>
            <div className="bulk-assignment-btn-container">
                {
                    props.misc.creating_assignment ? <img
                        alt="loading"
                        width="40"
                        height="40"
                        src={require("static/img/Theme-loader.gif")}
                    />
                        :
                        <ThemeButton primary onClick={handleCreateAssignment}>Create Assignment</ThemeButton>
                }
            </div>
        </div>
    );
};

export default connectStore({
    misc, inviteCode, activity, ui
})(BulkAssignmentModal)